import { getBaseUrl } from '@shared';

// Route that redeems a Clerk sign-in token and then forwards to `redirect_to`.
// Must be registered as a public route, otherwise SessionLoader bounces the
// signed-out recipient before the ticket can be redeemed.
export const AUTO_SIGN_IN_PATH = '/auth/link';

// Clerk's conventional query param name for a sign-in token.
export const AUTO_SIGN_IN_TICKET_PARAM = '__clerk_ticket';
export const AUTO_SIGN_IN_REDIRECT_PARAM = 'redirect_to';
export const AUTO_SIGN_IN_EMAIL_PARAM = 'email';

// Clerk enforces expiry server-side; this is the value we ask for when minting.
export const AUTO_SIGN_IN_EXPIRY_SECONDS = 3600;
export const AUTO_SIGN_IN_EXPIRY_MINUTES = AUTO_SIGN_IN_EXPIRY_SECONDS / 60;

// A client clock running fast would otherwise reject a token the server still
// considers valid. Errs towards attempting redemption and letting Clerk decide.
const EXPIRY_CLOCK_SKEW_SECONDS = 120;

export function clientAutoSignInLink({
  ticket,
  redirectTo,
  email,
}: {
  ticket: string;
  redirectTo: string;
  email?: string;
}): string {
  const params = new URLSearchParams({ [AUTO_SIGN_IN_REDIRECT_PARAM]: redirectTo });
  // Carried so the expired-link fallback can prefill the sign-in form. Safe to
  // include: the link is only ever delivered to this address.
  if (email) params.set(AUTO_SIGN_IN_EMAIL_PARAM, email);

  // The ticket goes in the fragment, not the query string. Fragments are never
  // sent to a server, so the credential stays out of access logs, proxy logs and
  // Referer headers. The redeem route reads it and strips it on mount.
  const fragment = new URLSearchParams({ [AUTO_SIGN_IN_TICKET_PARAM]: ticket });
  return `${getBaseUrl()}${AUTO_SIGN_IN_PATH}?${params.toString()}#${fragment.toString()}`;
}

// Where the redeem route sends someone whose ticket is expired or already used.
// `/` is the sign-in page in this app; it already reads both params, so the
// recipient lands on `redirectTo` once they have signed in manually.
export function autoSignInFallbackPath({
  redirectTo,
  email,
}: {
  redirectTo: string;
  email?: string | null;
}): string {
  const params = new URLSearchParams({ [AUTO_SIGN_IN_REDIRECT_PARAM]: redirectTo });
  if (email) params.set(AUTO_SIGN_IN_EMAIL_PARAM, email);
  params.set('reason', 'expired');
  return `/?${params.toString()}`;
}

// Any origin works as the probe target; only whether resolution stays on it
// matters. A `.invalid` host can never be a real destination.
const REDIRECT_PROBE_ORIGIN = 'https://redirect-probe.invalid';

/**
 * Reports whether a Clerk sign-in token is past its expiry, by reading the `exp`
 * claim the token carries. Malformed tokens, or tokens that are not sign-in
 * tokens, count as expired.
 *
 * Presentational only: it decides whether to bother showing the confirm screen,
 * never whether to grant access. Clerk remains the authority and re-checks on
 * redemption, so a tampered `exp` buys nothing. The signature is deliberately not
 * verified — that would need Clerk's keys and a network call for no gain.
 */
export function isAutoSignInTicketExpired(ticket: string, nowMs: number = Date.now()): boolean {
  const payload = decodeTicketPayload(ticket);
  if (!payload) return true;
  if (payload.st !== 'sign_in_token') return true;
  if (typeof payload.exp !== 'number') return true;
  return payload.exp * 1000 + EXPIRY_CLOCK_SKEW_SECONDS * 1000 <= nowMs;
}

function decodeTicketPayload(ticket: string): { exp?: number; st?: string } | null {
  const segments = ticket.split('.');
  if (segments.length < 2) return null;
  try {
    // atob is available in browsers and in Node's global scope from v16.
    const json = atob(segments[1].replace(/-/g, '+').replace(/_/g, '/'));
    const parsed = JSON.parse(json);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

// Guards against an emailed link forwarding the recipient off-site.
//
// Prefix checks alone are not enough: `/\evil.example` and `/\/evil.example`
// both start with a single `/`, but URL parsing treats `\` as `/` for special
// schemes, so they resolve to https://evil.example. Resolve the candidate and
// require the origin to survive, rather than trying to enumerate the escapes.
export function isInternalRedirectPath(path: string | null | undefined): boolean {
  if (!path) return false;
  if (!path.startsWith('/')) return false;
  try {
    return new URL(path, REDIRECT_PROBE_ORIGIN).origin === REDIRECT_PROBE_ORIGIN;
  } catch {
    return false;
  }
}
